import { Component, input, output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormArray, FormGroup } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FormFieldComponent } from '../form-field/form-field.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { ConfirmationService } from 'primeng/api';
import { DialogFieldDef } from '../../models/page-config.model';

@Component({
  selector: 'app-dynamic-accordion-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AccordionModule,
    ButtonModule,
    TooltipModule,
    FormFieldComponent,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    RadioButtonModule,
    CheckboxModule,
    ToggleSwitchModule,
    DatePickerModule
  ],
  templateUrl: './dynamic-accordion-form.component.html'
})
export class DynamicAccordionFormComponent {
  formArray    = input.required<FormArray>();
  entityName   = input<string>('Item');
  fields       = input<DialogFieldDef[]>([]);
  requiredFields = input<string[]>([]);

  activeIndex = signal<any>(0);
  deleteItem  = output<number>();
  addItem     = output<void>();

  private confirmationService = inject(ConfirmationService);

  getOptions(field: DialogFieldDef): any[] {
    if (!field.options) return [];
    return typeof field.options === 'function' ? field.options() : field.options;
  }

  headerLabel(index: number): string {
    const firstField = this.fields()[0];
    const nameVal = firstField
      ? (this.formArray().at(index) as FormGroup).get(firstField.key)?.value
      : null;
    return nameVal || `${this.entityName()} ${index + 1}`;
  }

  colClass(field: DialogFieldDef): string {
    const span = field.colSpan ?? 1;
    if (span >= 3) return 'md:col-span-3';
    if (span === 2) return 'md:col-span-2';
    return 'md:col-span-1';
  }

  onDeleteClick(index: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const label = this.headerLabel(index);
    this.confirmationService.confirm({
      header: 'Confirm Deletion',
      message: `Are you sure you want to remove "${label}"?`,
      icon: 'pi pi-exclamation-triangle text-red-500',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.activeIndex.set(-1);
        this.deleteItem.emit(index);
        setTimeout(() => this.activeIndex.set(Math.max(0, index - 1)), 0);
      }
    });
  }

  onAdd(): void {
    this.addItem.emit();
    setTimeout(() => this.activeIndex.set(this.formArray().length - 1), 0);
  }

  getControl(index: number, key: string) {
    return (this.formArray().at(index) as FormGroup).get(key);
  }
}
